/**
 * workQueueLogic — pure state + label derivations behind the "Next up" guided
 * work-through flow (docs/crm-work-queue-plan.md). The server sends DATA
 * (reason kinds + timestamps); every recruiter-facing word is rendered here,
 * and the advance/skip/persist math lives here — React-free and directly
 * testable (the safetyNetLane pattern). No imports that reach the axios
 * client, so the tests need no api mock.
 */
import { DETECTOR_LABEL } from './pages/safetyNetLane';
import type { CoverageInfo } from './components/CoverageBadge';
import {
  OUTCOME_LABELS,
  SEND_WINDOW_END_HOUR,
  SEND_WINDOW_START_HOUR,
  bucketLabel,
  shortWhen,
} from './outreach/outreachLogic';
import { nothingToWorkLine } from './outreach/outreachSurfaces';
import type {
  OutreachLoggableOutcome,
  OutreachOutcomeResponse,
  WorkQueueListCounts,
  WorkQueueOutreachRef,
} from './outreach/types';
import type { RecruitScope } from './recruit/types';
import { briefReasonLabel } from './brief/briefLogic';
import type { Brief, BriefState } from './brief/types';

export interface WorkQueueReason {
  kind: string;
  at?: string | null;
  detector?: string;
  detectors?: string[];
  attempt_count?: number | null;
  review_kind?: string;
  /**
   * Study sessions (the recruitment list, docs/plans/crm-recruitment-list-plan.md B5): the row's
   * rank (KIND_RANK + ELIGIBILITY_SHIFT), so the viewer's own leads can go first WITHIN a rank.
   */
  rank?: number;
  /** `waiting` only: next_try | snoozed | callback_later | agent_active. */
  why?: string | null;
  /** `open` only: the study the lead is open on ("Open on <study>"). */
  study_title?: string | null;
  /**
   * The attention tier (backend services/crmAttention.js, owner 2026-09-28): 1 = needs action now
   * (someone waiting on us, a live conversation, a hot new lead), 2 = keep trying (nobody is
   * waiting), 3 = nothing due (the sheet's waiting and open rows). Absent on older servers.
   */
  tier?: number;
  /** `conversation` and `follow_up`: when the lead last wrote to us (null = never). */
  last_inbound_at?: string | null;
}

/**
 * Which attention tier a session serves: `now` (the default: needs action now), `later` (keep
 * trying) or `all`. A study session's filters carry it too (WorkQueueStudyFilters.tier).
 */
export type WorkQueueTier = 'now' | 'later' | 'all';

/** The reason's tier: its own when the server sent one, else what its kind always is. */
export function reasonTier(reason: Pick<WorkQueueReason, 'kind' | 'tier'> | null | undefined): number {
  if (!reason) return 3;
  if (reason.tier === 1 || reason.tier === 2 || reason.tier === 3) return reason.tier;
  if (reason.kind === 'waiting' || reason.kind === 'open') return 3;
  return KEEP_TRYING_KINDS.has(reason.kind) ? 2 : 1;
}

/** The kinds that are always keep-trying work (safety_net is tiered by its catches). */
const KEEP_TRYING_KINDS: ReadonlySet<string> = new Set([
  'list_untouched', 'list_retry', 'not_contacted', 'follow_up', 'stale',
]);

export interface WorkQueueItem {
  key: string;
  href: string;
  contact_id: string;
  name: string;
  lifecycle_status: string | null;
  owner_user_id: string | null;
  study_title: string | null;
  /** The landing-form verdict the lead ARRIVED with (eligible | needs_review |
   *  pending | not_eligible | null). It is what re-ranks the queue server-side
   *  (ELIGIBILITY_SHIFT in crmWorkQueue.js), so the bar shows it: the recruiter
   *  should be able to see why this lead is the one on deck. */
  incoming_eligibility?: string | null;
  /**
   * Set ONLY when this lead is in the viewer's queue because its owner is out
   * of office today (docs/crm-out-of-office-coverage-plan.md). Ownership is not
   * transferred — the badge on the row says whose lead it really is. Absent on
   * the viewer's own work, so its presence alone is the "covering" test.
   */
  coverage?: CoverageInfo | null;
  /**
   * Set when this lead is an OPEN member of an outreach list
   * (docs/crm-outreach-lists-plan.md §B5). The server attaches it to every
   * queue item for that lead — a reply from a list lead is still a
   * `needs_reply` item, it just also carries the list — so its presence is
   * what swaps the bar's Done/Snooze for the outcome control: a list lead is
   * finished by LOGGING what happened, never by a session-only "Done".
   */
  call_list?: WorkQueueOutreachRef | null;
  /**
   * The AI brief for this lead (docs/plans/crm-brief-plan.md B3.7), attached by the server only while
   * briefs are on and the lead has one. Present = the bar's reason line is the brief's why-line.
   */
  brief?: Brief | null;
  reason: WorkQueueReason;
}

/** The work-queue response's switch: `brief_state`. Absent on older servers (read as off). */
export type WorkQueueBriefState = BriefState;

/** True when the viewer holds this row only because its owner is away. */
export const isCovering = (item: WorkQueueItem | null | undefined): boolean => !!item?.coverage;

/**
 * Own work first, covered work after — a STABLE partition, not a re-rank.
 *
 * Why a partition and not a sort key: the server has already ranked the queue
 * (urgency, then the per-lead ELIGIBILITY_SHIFT), and that ranking is the
 * product decision. All this adds is the "all else equal" rule the plan asks
 * for — when a recruiter's queue widens to cover an absent teammate, their OWN
 * book should not get pushed down the list by someone else's. Within each
 * group the server's order survives untouched, because Array#sort is required
 * to be stable (ES2019) and the comparator returns 0 for every same-group pair.
 *
 * What this deliberately CANNOT do: change which reason wins a lead. The queue
 * dedups to one row per contact SERVER-side (that is why the eligibility shift
 * is per-lead), so by the time a list reaches here every contact_id appears at
 * most once and there is nothing left to pick between. Reordering rows can
 * never reach that decision — keep it that way; a client-side dedup here would
 * hand the same lead a different reason than the bar's label says.
 *
 * STUDY SESSIONS (the recruitment list, docs/plans/crm-recruitment-list-plan.md
 * B5) pass the viewer's id: the sheet's default scope is everyone on the study
 * (decision 3), so within each run of rows of EQUAL rank the viewer's own leads
 * move ahead of everyone else's, the same "all else equal" rule one step finer.
 * Ranks never mix: a run is consecutive rows with the same numeric
 * `reason.rank`, so a sheet sorted by another column keeps its order, and a row
 * without a rank is never moved. Without `meId` (every other session) this is
 * exactly the partition above.
 */
export function orderOwnWorkFirst(items: WorkQueueItem[], meId?: string | null): WorkQueueItem[] {
  const ordered = meId ? mineFirstWithinRank(items, meId) : items.slice();
  return ordered.sort((a, b) => Number(isCovering(a)) - Number(isCovering(b)));
}

/** A row's numeric rank, or null when it carries none (every non-study item). */
const rankOf = (item: WorkQueueItem): number | null => {
  const r = item.reason.rank;
  return typeof r === 'number' && Number.isFinite(r) ? r : null;
};

/** The viewer's own rows first within each run of equal rank; nothing crosses a run. */
function mineFirstWithinRank(items: WorkQueueItem[], meId: string): WorkQueueItem[] {
  const out: WorkQueueItem[] = [];
  let start = 0;
  while (start < items.length) {
    const rank = rankOf(items[start]);
    let end = start + 1;
    if (rank !== null) {
      while (end < items.length && rankOf(items[end]) === rank) end += 1;
    }
    const run = items.slice(start, end);
    for (const it of run) {
      if (it.owner_user_id === meId) out.push(it);
    }
    for (const it of run) {
      if (it.owner_user_id !== meId) out.push(it);
    }
    start = end;
  }
  return out;
}

/**
 * Whose leads a session works, as the picker's raw selection:
 *   []                            -> EVERYONE (network-wide) — the multiselect's
 *                                    "Any" row, which is the toggle the owner
 *                                    asked for
 *   ['<uuid>', …, 'unassigned']   -> those recruiters (+ unowned leads)
 * The default selection is [me, 'unassigned'] — the CRM's historical scope.
 */
export type OwnerSelection = string[];

export const UNASSIGNED = 'unassigned';

/**
 * The server token that opts a scope into out-of-office coverage —
 * "…and whatever I'm holding for someone who is out today".
 * Parsed by backend/src/services/crmOwnerScope.js (COVERING_TOKEN).
 */
export const COVERING = 'covering';

/**
 * The `?owners=` param for a selection. Empty selection = network-wide.
 *
 * WHY `meId`: the server defaults `covering` to TRUE for a viewer's implicit
 * default scope, but to FALSE for an EXPLICIT `?owners=` list — because
 * "show me only Dana's leads" is a narrow question and silently widening it
 * would be wrong. This picker, however, ALWAYS sends an explicit list (Today
 * seeds it to [me, 'unassigned']), so without this the queue would never once
 * ask for coverage and the feature would be invisible on the guided flow —
 * while Today's own buckets, which send no owners param at all, showed the
 * covered leads. Clicking "Start working" would then skip exactly the leads
 * the page had just badged.
 *
 * So: re-assert the default. A selection that includes the viewer means "my
 * work" and therefore also means the work they are covering. A selection that
 * does NOT include them is someone else's desk, and stays literal.
 */
export const ownersParam = (selection: OwnerSelection, meId?: string | null): string => {
  if (selection.length === 0) return 'all';
  const includesMe = !!meId && selection.includes(meId);
  return (includesMe ? [...selection, COVERING] : selection).join(',');
};

/** The default selection for a viewer: their leads + unassigned. */
export const defaultOwnerSelection = (userId: string | null | undefined): OwnerSelection =>
  userId ? [userId, UNASSIGNED] : [UNASSIGNED];

/**
 * Short summary for the button/bar ("Everyone", "My leads", "Dana +1").
 * `nameOf` resolves a user id to a display name; `meId` marks the viewer.
 */
export function ownerSummary(
  selection: OwnerSelection,
  nameOf: (id: string) => string | undefined,
  meId?: string | null,
): string {
  if (selection.length === 0) return 'Everyone';
  const label = (v: string) => (v === UNASSIGNED ? 'Unassigned' : v === meId ? 'My leads' : nameOf(v) || 'Someone');
  const mineAndUnassigned = meId
    && selection.length === 2
    && selection.includes(meId)
    && selection.includes(UNASSIGNED);
  if (mineAndUnassigned) return 'My leads';
  if (selection.length === 1) return label(selection[0]);
  return `${label(selection[0])} +${selection.length - 1}`;
}

/**
 * What a session is working through. `call_list` is an outreach list
 * ("Working: <list name>", docs/crm-outreach-lists-plan.md §B6): the same
 * queue engine narrowed by `?list=`, re-fetched after every logged outcome
 * rather than walked as a frozen snapshot. `study` is a study's recruitment
 * list ("Working: <study> recruitment list",
 * docs/plans/crm-recruitment-list-plan.md B5): the sheet's own rows in the
 * sheet's order, handed over at start (startRowsState), refilled once from the
 * sheet's filters.
 */
export type WorkQueueSource = 'all' | 'safety_net' | 'call_list' | 'study';

/** The study a `study` session works. `title` is the name recruiters know it by (internal title first). */
export interface WorkQueueStudyRef {
  id: string;
  slug: string;
  title: string;
}

/**
 * The sheet's filters a `study` session started from: its one silent refill
 * re-reads the sheet with exactly these, minus the rows the session already
 * handled (plan B5).
 */
export interface WorkQueueStudyFilters {
  /** The sheet's owner picker: [] = everyone on the study (decision 3). */
  owners: OwnerSelection;
  scope: RecruitScope;
  /** The status chips; null = no status filter. */
  statuses: string[] | null;
  /** The search box; null = none. */
  q: string | null;
  showArchived: boolean;
  onlyNeedsAction: boolean;
  /** The recruiter confirmed "include the waiting ones" (rank 7 rows ride along). */
  includeWaiting: boolean;
  /**
   * The attention tier the session serves (recruitLogic rowsToQueueItems): `now` (needs action
   * now, the default), `later` (keep trying) or `all`. Absent on a session stored before the tiers:
   * read as `now` unless includeWaiting (then everything due, as it was).
   */
  tier?: WorkQueueTier | null;
}

export interface WorkQueueState {
  source: WorkQueueSource;
  /** The selection this session was started with — refills reuse it.
   *  null = the server's default scope (caller's leads + unassigned). */
  owners: OwnerSelection | null;
  items: WorkQueueItem[];
  index: number;
  /** contact_ids worked ("Done") this session — excluded from the refill. */
  handled: string[];
  /** contact_ids skipped this session — also excluded from the refill. */
  skipped: string[];
  startedAt: string;
  /** call_list only: the list uuid, or 'mine' for every list I'm on. */
  listId?: string | null;
  /** call_list only: the list's name for "Working: …" (null = several lists). */
  listName?: string | null;
  /** call_list only: the server's count of my open members on the list —
   *  "42 left". Server truth, NOT items.length - index: the list keeps
   *  changing under the session (replies arrive, next tries come due). */
  remaining?: number | null;
  /** call_list only: outcomes logged today — "9 done today". */
  doneToday?: number | null;
  /** study only: the study being worked ("Working: <title> recruitment list"). */
  study?: WorkQueueStudyRef | null;
  /** study only: the sheet's filters, which the refill reuses. The sheet's owner
   *  choice lives here too: a study session leaves `owners` null. */
  studyFilters?: WorkQueueStudyFilters | null;
  /** study only: where "Back to the list" goes (the sheet the session started from). */
  returnTo?: string | null;
  /**
   * `all` and `safety_net` sessions: the attention tier being served (owner 2026-09-28: "urgent
   * first, then offer the rest"). Absent = `now`. A `study` session's tier is its filters'.
   */
  tier?: WorkQueueTier | null;
  /**
   * How many keep-trying leads the server (or the sheet) counted the last time this session read
   * its queue: the finished state's "Keep trying (N)" offer. null = unknown (no offer number).
   */
  keepTrying?: number | null;
}

/** Compact relative span: "25m", "3h", "4d". */
const rel = (iso: string | null | undefined, now: Date): string | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  const span = Math.abs(now.getTime() - ms);
  const m = Math.round(span / 60e3);
  if (m < 60) return `${Math.max(1, m)}m`;
  const h = Math.round(span / 3600e3);
  if (h < 48) return `${h}h`;
  return `${Math.round(span / 86400e3)}d`;
};

/**
 * The bar's one-line "why this lead is up next".
 *
 * `ref` is the item's outreach-list ref, when it has one. It only changes the
 * words for the two list kinds — a reply from a list lead still reads "Owes a
 * reply", because that IS the work; the list is shown beside it, not instead.
 *
 * `brief` is the item's AI brief when it has one (item.brief): its why-line replaces the rule-based
 * words (crm-brief plan B6), and the kind label stays the caller's tooltip. A brief with a blank
 * why-line falls through to the rule-based label.
 */
export function reasonLabel(
  reason: WorkQueueReason,
  now: Date = new Date(),
  ref?: WorkQueueOutreachRef | null,
  brief?: Brief | null,
): string {
  if (brief) {
    const fromBrief = briefReasonLabel(brief, now);
    if (fromBrief) return fromBrief;
  }
  const r = rel(reason.at, now);
  switch (reason.kind) {
    // Outreach-list kinds (plan §B5). "Contacted — no outcome logged" is the
    // honest label for someone we called or texted outside the list's own
    // text without logging what happened: they are not "Not called yet" any
    // more, and they have not used up a try either.
    case 'list_untouched':
      return ref?.bucket === 'contacted_no_outcome' ? 'Contacted — no outcome logged' : 'Not called yet';
    case 'list_retry':
      if (ref?.bucket === 'contacted_no_outcome') return 'Contacted — no outcome logged';
      return ref ? bucketLabel('retry', ref) : 'Try again';
    case 'callback_due': {
      if (!reason.at) return 'Booked call due';
      const overdue = new Date(reason.at).getTime() <= now.getTime();
      return overdue ? `Booked call — ${r} overdue` : `Booked call in ${r}`;
    }
    case 'agent_review':
      return `Agent review — ${String(reason.review_kind || 'decision').replace(/_/g, ' ')}`;
    case 'needs_reply':
      return r ? `Owes a reply — waiting ${r}` : 'Owes a reply';
    case 'new_lead': {
      // A hot new lead (attention tier 1): came in, or re-submitted a form, within the window.
      const tail = reason.attempt_count ? 'no answer yet' : 'first touch';
      return r ? `New lead — arrived ${r} ago, ${tail}` : `New lead — ${tail}`;
    }
    case 'conversation': {
      // A live conversation gone quiet (attention tier 1): keep it going.
      const wrote = rel(reason.last_inbound_at, now);
      return wrote ? `Conversation going quiet — they wrote ${wrote} ago` : 'Conversation going quiet';
    }
    case 'not_contacted':
      return r ? `Never contacted — came in ${r} ago` : 'Never contacted';
    case 'safety_net': {
      const label = (reason.detector && DETECTOR_LABEL[reason.detector]) || 'Left behind';
      const extra = (reason.detectors?.length || 0) - 1;
      return extra > 0 ? `${label} (+${extra} more)` : label;
    }
    case 'follow_up': {
      // Keep trying (attention tier 2): nobody is waiting on us.
      const wrote = rel(reason.last_inbound_at, now);
      if (wrote) return `Last wrote ${wrote} ago — follow-up due`;
      const tries = reason.attempt_count ? ` (${reason.attempt_count} ${reason.attempt_count === 1 ? 'try' : 'tries'} so far)` : '';
      return `No answer yet — follow-up due${tries}`;
    }
    case 'stale':
      return r ? `Going quiet — ${r} since last activity` : 'Going quiet';
    // Recruitment list sessions (plan B5): the two kinds a sheet row can carry
    // that the queue never serves on its own.
    case 'waiting':
      return waitingLabel(reason.why, reason.at, { now });
    case 'open':
      return reason.study_title ? `Open on ${reason.study_title}` : 'Open on this study';
    default:
      return 'Needs attention';
  }
}

/**
 * The incoming-verdict chip for a queue row — shown ONLY for the two verdicts
 * that actually move a lead's position (the server's ELIGIBILITY_SHIFT).
 * `needs_review`, an incomplete screener and a missing verdict are unknowns,
 * rank neutrally, and would be noise on a pill this small.
 */
export function eligibilityChip(
  incoming: string | null | undefined,
): { label: string; tone: 'good' | 'bad' } | null {
  if (incoming === 'eligible') return { label: 'Eligible', tone: 'good' };
  if (incoming === 'not_eligible') return { label: 'Not eligible', tone: 'bad' };
  return null;
}

// --- Snooze from the bar ---------------------------------------------------
//
// "Done" is session-only: it advances the bar and keeps the lead out of this
// sitting's one refill, and that is ALL — nothing reaches the server, so a lead
// you looked at and left alone is back the next time you click Work through.
// Snoozing is the durable "nothing needed here, see you later": it writes
// crm.contacts.snoozed_until, which the worklist buckets and the safety net
// both honor, and which a new inbound clears automatically so a deferred lead
// can never be buried while the patient is trying to reach us.

/**
 * When a bar snooze should end: local midnight at the START of the day `days`
 * from today, so the lead returns at the top of that morning rather than at
 * whatever moment you happened to defer it. 1 = tomorrow, 7 = a week out.
 *
 * The date is advanced BEFORE the clock is zeroed — the other order lands an
 * hour off across a DST change.
 */
export function snoozeUntilDays(days: number, now: Date = new Date()): string {
  const d = new Date(now);
  d.setDate(d.getDate() + Math.max(1, Math.floor(days)));
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/** The bar's two quick picks; anything else goes through the days box. */
export const SNOOZE_PICKS: { label: string; days: number }[] = [
  { label: 'Tomorrow', days: 1 },
  { label: 'Next week', days: 7 },
];

/** Parse the "put it out X days" box — a whole number of days, 1–365. */
export function parseSnoozeDays(raw: string): number | null {
  const n = Number(String(raw).trim());
  if (!Number.isInteger(n) || n < 1 || n > 365) return null;
  return n;
}

/** "Fri, Aug 15" — the day the lead comes back. */
export function snoozeReturnLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

/**
 * Why the row on deck cannot be snoozed OFF the queue, or null if it can.
 *
 * Two queue sources do not read `snoozed_until` at all, so offering a snooze
 * there would defer the lead and hand them straight back — the exact
 * frustration this button exists to fix. Blocking with the real reason beats
 * a control that silently does nothing.
 */
export function snoozeBlockedReason(kind: string, callList?: WorkQueueOutreachRef | null): string | null {
  // A list lead's "see you later" is the list's own Try later: it schedules
  // the next try ON the list, where the recruiter will be served it again. A
  // contact-level snooze would hide them from Today while the list still
  // counts them as due — two clocks for one person.
  if (callList || kind === 'list_untouched' || kind === 'list_retry') {
    return 'This person is on an outreach list — use Try later on the list instead of snoozing.';
  }
  if (kind === 'callback_due') {
    return 'A booked call is an appointment — reschedule or cancel it on the lead instead.';
  }
  if (kind === 'agent_review') {
    return 'An agent review clears when you resolve it, not by snoozing the lead.';
  }
  return null;
}

/** How one queue row reads in the list: what you did with it, or where it sits. */
export type QueueRowStatus = 'done' | 'skipped' | 'current' | 'upcoming';

/**
 * The status of row `i`. Pure. Derived from the session ledgers first and the
 * cursor only as a fallback, so jumping around the list (goTo) can't relabel
 * rows you never actually touched.
 */
export function rowStatus(s: WorkQueueState, i: number): QueueRowStatus {
  const item = s.items[i];
  if (!item) return 'upcoming';
  if (i === s.index) return 'current';
  if (s.handled.includes(item.contact_id)) return 'done';
  if (s.skipped.includes(item.contact_id)) return 'skipped';
  return 'upcoming';
}

/** Rows worked so far — the list's "3 of 17 done" line. */
export const doneCount = (s: WorkQueueState | null): number => (s ? s.handled.length : 0);

export const currentItem = (s: WorkQueueState | null): WorkQueueItem | null =>
  s ? s.items[s.index] ?? null : null;

export const progress = (s: WorkQueueState | null): { position: number; total: number } =>
  s ? { position: Math.min(s.index + 1, s.items.length), total: s.items.length } : { position: 0, total: 0 };

/** "Done" on the current item: remember it as handled, move on. */
export function advanceState(s: WorkQueueState): WorkQueueState {
  const cur = currentItem(s);
  return {
    ...s,
    handled: cur && !s.handled.includes(cur.contact_id) ? [...s.handled, cur.contact_id] : s.handled,
    index: s.index + 1,
  };
}

/** "Skip": same move, different ledger (skips are not recycled by the refill). */
export function skipState(s: WorkQueueState): WorkQueueState {
  const cur = currentItem(s);
  return {
    ...s,
    skipped: cur && !s.skipped.includes(cur.contact_id) ? [...s.skipped, cur.contact_id] : s.skipped,
    index: s.index + 1,
  };
}

/** Leads this session already touched — the refill filters them out. */
export const sessionExcludes = (s: WorkQueueState): Set<string> =>
  new Set([...s.handled, ...s.skipped]);

/** Swap in a refill list (already exclude-filtered), restarting at the top.
 *  Applies the own-work-first partition, so a refill that pulls in an absent
 *  teammate's leads does not bury the viewer's own. A study session passes the
 *  viewer (`meId`) so its refill keeps "my leads first within each rank". */
export const withRefill = (s: WorkQueueState, items: WorkQueueItem[], meId?: string | null): WorkQueueState =>
  ({ ...s, items: orderOwnWorkFirst(items, meId), index: 0 });

// --- Outreach lists: working a list through the bar ------------------------
//
// docs/crm-outreach-lists-plan.md §B6. A list session is the SAME queue engine
// narrowed by `?list=`; what differs is how it moves. A normal session walks a
// snapshot and refills once when it runs dry. A list session re-fetches after
// EVERY logged outcome, because the list changes under the recruiter the whole
// time — a reply jumps the line, a next try comes due, a teammate's share-out
// lands — and the only honest "who's next" is the server's answer right now.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A `?list=` value the server accepts: a list uuid, or 'mine'. Anything else
 *  would come back a 400, so it is refused before the request. */
export const isListParam = (v: unknown): v is string =>
  typeof v === 'string' && (v === 'mine' || UUID_RE.test(v));

/**
 * The name for "Working: …". A single list's name comes off its own rows;
 * 'mine' only gets a name when every row happens to be on the same list —
 * otherwise null, and the bar says "your outreach lists".
 */
export function listNameFor(items: WorkQueueItem[], listId: string | null | undefined): string | null {
  const refs = items.map((i) => i.call_list).filter((r): r is WorkQueueOutreachRef => !!r);
  if (listId && listId !== 'mine') {
    return refs.find((r) => r.list_id === listId)?.list_name ?? null;
  }
  const ids = new Set(refs.map((r) => r.list_id));
  return ids.size === 1 ? refs[0].list_name : null;
}

/**
 * Fold a fresh `?list=` fetch into a running list session.
 *
 * - Rows this session already WORKED (logged or skipped) stay frozen at the
 *   head, in the order they were worked, so the list view still shows what
 *   you did (a check, or a skip behind "Show skipped").
 * - The fetch is appended minus everyone in those two ledgers. The server
 *   has usually dropped a logged lead already; the ledger covers the lag, and
 *   it keeps a SKIPPED lead from coming straight back — skip is "not now",
 *   and within a sitting it has to mean it.
 * - The cursor lands on the first unworked row, which is the server's top
 *   pick. Nothing unworked left = the cursor runs off the end, which is the
 *   caller's "finished" signal (currentItem → null).
 *
 * Pure: never mutates `s` or `fetched`.
 */
export function applyListRefetch(
  s: WorkQueueState,
  fetched: WorkQueueItem[],
  counts?: WorkQueueListCounts | null,
): WorkQueueState {
  const worked = sessionExcludes(s);
  const seen = new Set<string>();
  const kept: WorkQueueItem[] = [];
  for (const it of s.items) {
    if (worked.has(it.contact_id) && !seen.has(it.contact_id)) {
      kept.push(it);
      seen.add(it.contact_id);
    }
  }
  const fresh: WorkQueueItem[] = [];
  for (const it of fetched) {
    if (worked.has(it.contact_id) || seen.has(it.contact_id)) continue;
    fresh.push(it);
    seen.add(it.contact_id);
  }
  const items = [...kept, ...fresh];
  // One list: the fetch has its current name (a rename lands mid-session).
  // 'mine': recomputed over every row, so a second list joining turns the
  // header back into "your outreach lists" instead of keeping a stale name.
  const listName = s.listId === 'mine'
    ? listNameFor(items, 'mine')
    : listNameFor(fetched, s.listId) ?? s.listName ?? null;
  return {
    ...s,
    items,
    index: kept.length,
    listName,
    remaining: counts ? counts.remaining : s.remaining ?? null,
    doneToday: counts ? counts.done_today : s.doneToday ?? null,
  };
}

/** The first row at or after `from` this session has not worked (or length). */
export function nextUnworkedIndex(s: WorkQueueState, from: number): number {
  const worked = sessionExcludes(s);
  for (let i = Math.max(0, from); i < s.items.length; i += 1) {
    if (!worked.has(s.items[i].contact_id)) return i;
  }
  return s.items.length;
}

/**
 * Record an outcome logged for `contactId` without a re-fetch (the fallback
 * when the fetch fails, and the whole story for a normal session). The lead
 * joins `handled`; if they were the row on deck the cursor moves to the next
 * unworked row, otherwise it stays put — logging a detour lead from its lead
 * page must not yank the bar off the lead you were on.
 */
export function markOutcomeLogged(s: WorkQueueState, contactId: string): WorkQueueState {
  const handled = s.handled.includes(contactId) ? s.handled : [...s.handled, contactId];
  const next = { ...s, handled };
  const wasCurrent = currentItem(s)?.contact_id === contactId;
  return wasCurrent ? { ...next, index: nextUnworkedIndex(next, s.index + 1) } : next;
}

/**
 * How many replies a poll found that this session is not showing yet — the
 * "1 new reply — up next" chip. A reply counts when the lead is not already on
 * the list as a reply and was not worked this sitting (the next re-fetch would
 * leave those out, so counting them would promise something the bar won't do).
 */
export function newReplyCount(s: WorkQueueState | null, fetched: WorkQueueItem[]): number {
  if (!s) return 0;
  const worked = sessionExcludes(s);
  const shownAsReply = new Set(
    s.items.filter((i) => i.reason.kind === 'needs_reply').map((i) => i.contact_id),
  );
  const counted = new Set<string>();
  for (const it of fetched) {
    const isReply = it.reason.kind === 'needs_reply' || it.call_list?.bucket === 'replied';
    if (!isReply || worked.has(it.contact_id) || shownAsReply.has(it.contact_id)) continue;
    counted.add(it.contact_id);
  }
  return counted.size;
}

/** "1 new reply — up next" / "3 new replies — up next". */
export const newReplyLabel = (n: number): string =>
  `${n} new ${n === 1 ? 'reply' : 'replies'} — up next`;

/** Rows a poll found that the session could still work (the finished state's "Keep going"). */
export function freshCount(s: WorkQueueState | null, fetched: WorkQueueItem[]): number {
  if (!s) return fetched.length;
  const worked = sessionExcludes(s);
  return new Set(fetched.filter((i) => !worked.has(i.contact_id)).map((i) => i.contact_id)).size;
}

/** The counter on the pill: "42 left · 9 done today" (the done half drops at zero). */
export function listCounterLabel(remaining: number | null | undefined, doneToday: number | null | undefined): string {
  const left = typeof remaining === 'number' ? `${remaining.toLocaleString()} left` : 'Your list';
  return doneToday ? `${left} · ${doneToday.toLocaleString()} done today` : left;
}

/**
 * The sentence for a list with nobody to call right now — the bar's finished
 * state. The words themselves live in ONE place, outreachSurfaces'
 * nothingToWorkLine, which Today and the list pages use too, so the bar and the
 * pages can never describe the same list two ways. The bar only differs when it
 * has no counts yet (the first poll hasn't landed): it says the plain fact
 * rather than "couldn't check", because nothing has failed.
 * `fmt` renders the next-try time (injected so tests are deterministic).
 */
export function listEmptyLine(
  counts: WorkQueueListCounts | null | undefined,
  fmt: (iso: string) => string = (iso) => shortWhen(iso),
): string {
  if (!counts) return 'Everyone on your list has been tried.';
  return nothingToWorkLine(counts, fmt);
}

/**
 * The finished state's sentence when the list ran out only because the
 * recruiter SKIPPED people who are still due — or null when that is not what
 * happened. A skip keeps someone out of the rest of the sitting, so skipping
 * the last few untried people ends the list; listEmptyLine would then say
 * "Nobody on your list needs a call right now" (or "Everyone has been tried"),
 * which is false. This says what is actually true, and the bar offers
 * "Go back to them" beside it.
 *
 * "Still due" is the server's count of people callable right now (never
 * tried, due a retry, replied, a booked call due, an agent review). It is
 * capped by the skips: someone skipped here may since have been worked by a
 * teammate, and the sentence must not promise more people than it can serve.
 */
export function skippedStillDueLine(
  counts: WorkQueueListCounts | null | undefined,
  skipped: number,
): string | null {
  if (!counts || skipped <= 0) return null;
  const due = (counts.untouched || 0) + (counts.retry || 0) + (counts.replied || 0)
    + (counts.callback_due || 0) + (counts.agent_review || 0);
  const n = Math.min(skipped, due);
  if (n <= 0) return null;
  return n === 1
    ? 'You skipped 1 person who is still due.'
    : `You skipped ${n.toLocaleString()} people who are still due.`;
}

/** Where "Back to the list" goes: the list page, or the lists index for 'mine'. */
export const listHref = (listId: string | null | undefined): string =>
  listId && listId !== 'mine' ? `/crm/outreach/${listId}` : '/crm/outreach';

/**
 * The rows the list view shows. In a list session skipped rows fold behind a
 * "Show skipped" row — the list is long and a skip is "not now", so they are
 * clutter until you want them back. Indices are the ORIGINAL ones, so a click
 * still goTo()s the right row. Normal sessions show everything, as before.
 */
export function visibleRows<T extends { status: QueueRowStatus }>(
  rows: T[],
  opts: { listMode: boolean; showSkipped: boolean },
): { rows: Array<T & { index: number }>; hiddenSkipped: number } {
  const all = rows.map((r, index) => ({ ...r, index }));
  if (!opts.listMode || opts.showSkipped) return { rows: all, hiddenSkipped: 0 };
  const kept = all.filter((r) => r.status !== 'skipped');
  return { rows: kept, hiddenSkipped: all.length - kept.length };
}

// --- Outreach lists: the outcome control's words and picks -----------------

/**
 * Which outcome the control's main button should offer, from the last thing I
 * did to this person since their last outcome: a text → "Sent a text"; a call
 * that rang out → "No answer". A call that connected tells us nothing about
 * how it went, so it falls back to the default like no touch at all (null).
 */
export function preselectOutcome(ref: WorkQueueOutreachRef | null | undefined): OutreachLoggableOutcome | null {
  const t = ref?.last_touch;
  if (!t) return null;
  if (t.channel === 'sms') return 'texted';
  const status = String(t.call_status || '').toLowerCase().replace(/_/g, '-');
  if (['no-answer', 'busy', 'failed', 'canceled', 'cancelled'].includes(status)) return 'no_answer';
  return null;
}

const STAGE_WORDS: Record<string, string> = {
  new: 'New',
  contacting: 'Contacting',
  not_interested: 'Not interested',
  unreachable: 'Unreachable',
  not_eligible: 'Not eligible',
};

/**
 * The confirmation line after an outcome lands — what happened to the PERSON
 * on the list and, when it moved, to the lead. Built only from the server's
 * answer, so it can never claim a stage change the server did not make.
 */
export function outcomeConfirmation(
  outcome: OutreachLoggableOutcome,
  res: OutreachOutcomeResponse | null | undefined,
  fmt: (iso: string) => string = (iso) => shortWhen(iso),
): string {
  const label = OUTCOME_LABELS[outcome];
  const effect = res?.lead_effect;
  const member = res?.member;
  if (effect?.skipped_reason === 'moved_study') {
    return `${label} logged. They've moved to another study, so they're off this list.`;
  }
  if (effect?.opted_out) {
    return 'Logged. They are opted out of texts and marked Unreachable.';
  }
  if (outcome === 'call_back' && member?.next_attempt_at) {
    return `Call back booked for ${fmt(member.next_attempt_at)}.`;
  }
  if (member?.state === 'done' && member.outcome === 'no_response') {
    return effect?.lifecycle_to
      ? `${label} logged. That was the last try — marked ${STAGE_WORDS[effect.lifecycle_to] || 'finished'}.`
      : `${label} logged. That was the last try — they're finished on this list.`;
  }
  if (effect?.lifecycle_to) {
    return `${label} logged. Moved to ${STAGE_WORDS[effect.lifecycle_to] || 'a new stage'}.`;
  }
  if (member?.state === 'open' && member.next_attempt_at) {
    return `${label} logged. Next try ${fmt(member.next_attempt_at)}.`;
  }
  return `${label} logged.`;
}

/** A quick pick in the Call back / Try later menus. */
export interface TimePick { label: string; iso: string }

/** Round up to the next quarter hour — tidy times read as booked, not computed. */
function ceilQuarter(d: Date): Date {
  const out = new Date(d);
  const m = out.getMinutes();
  const up = (15 - (m % 15)) % 15;
  out.setMinutes(m + up, 0, 0);
  return out;
}

/** Local `hour`:00 on the day `days` from `now` (date first, then the clock —
 *  the other order lands an hour off across a DST change). */
function atLocal(now: Date, days: number, hour: number): Date {
  const d = new Date(now);
  d.setDate(d.getDate() + days);
  d.setHours(hour, 0, 0, 0);
  return d;
}

/** "Call back at…" quick picks: Tomorrow 10am, In 2 hours. Recruiter-local time. */
export function callbackPicks(now: Date = new Date()): TimePick[] {
  return [
    { label: 'Tomorrow 10am', iso: atLocal(now, 1, 10).toISOString() },
    { label: 'In 2 hours', iso: ceilQuarter(new Date(now.getTime() + 2 * 3600e3)).toISOString() },
  ];
}

/** "Try later…" quick picks. All inside the server's 60-day ceiling. */
export function retryPicks(now: Date = new Date()): TimePick[] {
  return [
    { label: 'In 2 hours', iso: ceilQuarter(new Date(now.getTime() + 2 * 3600e3)).toISOString() },
    { label: 'Tomorrow morning', iso: atLocal(now, 1, 9).toISOString() },
    { label: 'In 3 days', iso: atLocal(now, 3, 9).toISOString() },
    { label: 'Next week', iso: atLocal(now, 7, 9).toISOString() },
  ];
}

/** The server refuses a try-later more than 60 days out. */
export const MAX_RETRY_DAYS = 60;

/**
 * Validate a hand-picked time (a datetime-local value): null when usable,
 * otherwise the sentence to show under the box.
 */
export function pickedTimeProblem(
  value: string,
  kind: 'call_back' | 'try_later',
  now: Date = new Date(),
): string | null {
  if (!value) return 'Pick a day and time.';
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return 'Pick a day and time.';
  if (t <= now.getTime()) return 'Pick a time in the future.';
  if (kind === 'try_later' && t > now.getTime() + MAX_RETRY_DAYS * 86400e3) {
    return `Pick a time within the next ${MAX_RETRY_DAYS} days.`;
  }
  return null;
}

// --- Calling hours -----------------------------------------------------------
//
// The CRM's calling window is 8am–9pm in the tenant's zone (the same quiet
// hours the texting side enforces, crmCompliance). The browser does not know
// the tenant's zone, so the CRM's day-bucketing default stands in for it.

export const CALLING_TZ = 'America/New_York';

/** 'before' 8am / 'after' 9pm in `tz`, or null inside calling hours. */
export function callingHoursBlock(now: Date = new Date(), tz: string = CALLING_TZ): 'before' | 'after' | null {
  let hour: number;
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: '2-digit', hourCycle: 'h23' })
      .formatToParts(now)
      .find((p) => p.type === 'hour');
    hour = Number(part?.value);
  } catch {
    hour = now.getHours();
  }
  if (!Number.isFinite(hour)) return null;
  if (hour < SEND_WINDOW_START_HOUR) return 'before';
  if (hour >= SEND_WINDOW_END_HOUR) return 'after';
  return null;
}

/** The bar's blocking line outside calling hours. */
export const callingHoursLine = (block: 'before' | 'after'): string =>
  block === 'after'
    ? "It's after 9pm — calling now needs a reason."
    : "It's before 8am — calling now needs a reason.";

/**
 * Window event the inbound-text feed fires when a lead on one of MY outreach
 * lists texts back (components/overlay/useSmsFeed). A list session listens
 * and polls at once, so the "new reply" chip does not wait out the minute.
 * An event rather than a second realtime subscription: one socket topic, one
 * subscriber.
 */
export const OUTREACH_REPLY_EVENT = 'crm:outreach-reply';

// --- Recruitment list sessions (docs/plans/crm-recruitment-list-plan.md B5) --
//
// "Work through" on a study's sheet starts the bar over exactly the rows on
// screen, in the sheet's order. The rows arrive already built into queue items
// (recruit/recruitLogic rowsToQueueItems), so every label above still applies;
// the two kinds only a sheet row carries, `waiting` and `open`, are worded
// here, and the waiting words are shared with the sheet's Reason cell so the
// bar and the sheet can never describe the same row two ways.

/** Whole local calendar days from `now`'s date to `d`'s (DST-safe: calendar fields, not hours). */
function localDayDiff(d: Date, now: Date): number {
  const a = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a - b) / 86400e3);
}

const clockTime = (d: Date): string => d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

/**
 * A moment, recruiter-local: "3:15 PM" today, "tomorrow 9:00 AM", "yesterday
 * 4:30 PM", "Tue 10:00 AM" later this week, "Oct 3, 10:00 AM" beyond. Empty for
 * a bad date.
 */
export function clockWords(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = clockTime(d);
  const days = localDayDiff(d, now);
  if (days === 0) return time;
  if (days === 1) return `tomorrow ${time}`;
  if (days === -1) return `yesterday ${time}`;
  if (days > 1 && days < 7) return `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${time}`;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${time}`;
}

/**
 * A day, recruiter-local: the time when it is today ("4:00 PM"), "tomorrow",
 * the weekday later this week ("Fri"), the date beyond ("Oct 3"). Empty for a
 * bad date.
 */
export function dayWords(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = localDayDiff(d, now);
  if (days === 0) return clockTime(d);
  if (days === 1) return 'tomorrow';
  if (days > 1 && days < 7) return d.toLocaleDateString(undefined, { weekday: 'short' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * The words for a `waiting` row (Part A rank 7: nothing is due yet): "Next try
 * Fri", "Snoozed until Mon", "Call booked Tue 10:00 AM", "Agent texting". `at`
 * is when the wait ends. The formatters are injected so tests are deterministic.
 */
export function waitingLabel(
  why: string | null | undefined,
  at: string | null | undefined,
  opts: { now?: Date; formatTime?: (iso: string) => string; formatDay?: (iso: string) => string } = {},
): string {
  const now = opts.now ?? new Date();
  const formatDay = opts.formatDay ?? ((iso: string) => dayWords(iso, now));
  const formatTime = opts.formatTime ?? ((iso: string) => clockWords(iso, now));
  switch (why) {
    case 'next_try':
      return at ? `Next try ${formatDay(at)}` : 'Waiting for the next try';
    case 'snoozed':
      return at ? `Snoozed until ${formatDay(at)}` : 'Snoozed';
    case 'callback_later':
      return at ? `Call booked ${formatTime(at)}` : 'Call booked';
    case 'agent_active':
      return 'Agent texting';
    default:
      return 'Waiting';
  }
}

/** "Neuropathy recruitment list": the bar's "Working: …" for a study session. */
export function studyLabelFor(s: WorkQueueState | null | undefined): string {
  const title = s?.study?.title?.trim();
  return title ? `${title} recruitment list` : 'Recruitment list';
}

/** A path inside the CRM: the only place "Back to the list" may lead. */
const isInternalCrmPath = (v: unknown): v is string =>
  typeof v === 'string' && (v === '/crm' || v.startsWith('/crm/') || v.startsWith('/crm?'));

/** Where "Back to the list" goes: the sheet the session started from, else the study's sheet. */
export function studyReturnHref(s: WorkQueueState | null | undefined): string {
  if (s?.returnTo && isInternalCrmPath(s.returnTo)) return s.returnTo;
  if (s?.study?.slug) return `/crm/recruit/${encodeURIComponent(s.study.slug)}`;
  return '/crm/recruit';
}

/** A study a session can be persisted with: a uuid, a slug, a title. */
export function isStudyRef(v: unknown): v is WorkQueueStudyRef {
  const r = v as Partial<WorkQueueStudyRef> | null | undefined;
  return !!r && typeof r === 'object'
    && typeof r.id === 'string' && UUID_RE.test(r.id)
    && typeof r.slug === 'string' && r.slug.length > 0
    && typeof r.title === 'string';
}

const STUDY_SCOPES: ReadonlySet<string> = new Set(['open', 'closed', 'all']);

const isStringList = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === 'string');

/** A stored filter set the refill can safely re-send. */
function isStudyFilters(v: unknown): v is WorkQueueStudyFilters {
  const f = v as Partial<WorkQueueStudyFilters> | null | undefined;
  if (!f || typeof f !== 'object') return false;
  if (!isStringList(f.owners)) return false;
  if (typeof f.scope !== 'string' || !STUDY_SCOPES.has(f.scope)) return false;
  if (f.statuses != null && !isStringList(f.statuses)) return false;
  if (f.q != null && typeof f.q !== 'string') return false;
  if (f.tier != null && !isWorkQueueTier(f.tier)) return false;
  return typeof f.showArchived === 'boolean'
    && typeof f.onlyNeedsAction === 'boolean'
    && typeof f.includeWaiting === 'boolean';
}

/** One of the three tiers a session can serve. */
export function isWorkQueueTier(v: unknown): v is WorkQueueTier {
  return v === 'now' || v === 'later' || v === 'all';
}

/**
 * A new `study` session over the sheet's rows (already built by recruitLogic
 * rowsToQueueItems, in the sheet's order after its filters), starting at the
 * first. With `meId` the viewer's own leads go first within each rank
 * (orderOwnWorkFirst); `filters` and `returnTo` ride along for the refill and
 * "Back to the list". The context navigates; this only builds the state.
 */
export function startRowsState(
  items: WorkQueueItem[],
  study: WorkQueueStudyRef,
  opts: {
    meId?: string | null;
    filters?: WorkQueueStudyFilters | null;
    returnTo?: string | null;
    now?: Date;
  } = {},
): WorkQueueState {
  return {
    source: 'study',
    owners: null,
    items: orderOwnWorkFirst(items, opts.meId),
    index: 0,
    handled: [],
    skipped: [],
    startedAt: (opts.now ?? new Date()).toISOString(),
    study: { id: study.id, slug: study.slug, title: study.title },
    studyFilters: opts.filters ?? null,
    returnTo: opts.returnTo ?? null,
  };
}

// --- sessionStorage persistence (per-tab; survives refresh mid-run) --------

// v2 (outreach lists): state gained `call_list` sessions. A v1 blob has no
// listId and would restore a list session that can't re-fetch — drop it.
// Study sessions (the recruitment list) are one more source under v2: every
// earlier v2 blob still parses, and an older bundle refuses the unknown source
// and drops the session, which is the safe direction.
const STORE_VERSION = 2;

const SOURCES: ReadonlySet<string> = new Set(['all', 'safety_net', 'call_list', 'study']);

export function serializeState(s: WorkQueueState): string {
  return JSON.stringify({ v: STORE_VERSION, state: s });
}

export function parseStoredState(raw: string | null): WorkQueueState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.v !== STORE_VERSION) return null;
    const s = parsed.state;
    if (!s || !Array.isArray(s.items) || typeof s.index !== 'number') return null;
    if (!SOURCES.has(s.source)) return null;
    if (!Array.isArray(s.handled) || !Array.isArray(s.skipped)) return null;
    if (s.source === 'call_list' && !isListParam(s.listId)) return null;
    // A study session without its study can neither refill nor say what it is
    // working; a filter set or way back that is not what startRowsState writes
    // is refused rather than re-sent to the server or navigated to.
    if (s.source === 'study' && !isStudyRef(s.study)) return null;
    if (s.source === 'study' && s.studyFilters != null && !isStudyFilters(s.studyFilters)) return null;
    if (s.returnTo != null && !isInternalCrmPath(s.returnTo)) return null;
    // The attention tier (a session stored before the tiers has none: read as `now`); a value
    // this bundle does not know drops the session rather than guessing what it served.
    if (s.tier != null && !isWorkQueueTier(s.tier)) return null;
    if (s.keepTrying != null && !(typeof s.keepTrying === 'number' && Number.isFinite(s.keepTrying))) return null;
    return s as WorkQueueState;
  } catch {
    return null;
  }
}
